import ConfirmationModal from '@components/ConfirmationModal'
import { useCreateTrip } from '@hooks/useCreateTrip'
import { getErrorMessage } from '@lib/apiError'
import { PageScroll } from '@components/Page'
import { Trip } from '@types'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { FlowContentArea, HeaderRow, HeaderSlot } from './CreateTripPage.styles'
import { CreateTripDraft, emptyDraft, isDraftEmpty } from './core/draft'
import { toPostTripPayload } from './core/payload'
import {
	CreateTripStep,
	STEPS,
	bottomBar,
	showsHeaderBack,
	stepIndicatorState,
	stepLabel,
} from './core/presentation'
import FlowShell from './FlowShell'
import NameStep from './NameStep'
import { CreateTripLocationState } from './paths'
import ReadyStep from './ReadyStep'
import WhenStep from './WhenStep'
import WhereStep from './WhereStep'
import { TestIds } from '@/testIds'

/**
 * The create-trip flow (ADR 0007) as a full page, styled like the shipped
 * mobile flow: build the trip by stacking the places you're going — length is
 * a consequence, not a question. Destinations → Start Date → Name It, every
 * step skippable like iOS; the trip is created after Name, and the Ready
 * screen that follows fills the card alone — no header, no bar, no way back.
 */
const CreateTripPage: React.FC = () => {
	const navigate = useNavigate()
	const source = (useLocation().state as CreateTripLocationState | null)?.source
	const createTrip = useCreateTrip()

	const [draft, setDraft] = useState<CreateTripDraft>(emptyDraft)
	const [step, setStep] = useState<CreateTripStep>('where')
	const [createdTrip, setCreatedTrip] = useState<Trip | null>(null)
	const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

	const stepIndex = STEPS.indexOf(step)

	const leaveToTrips = () => navigate('/trips')

	const handleClose = () => {
		if (isDraftEmpty(draft)) {
			leaveToTrips()
			return
		}
		setIsConfirmingDiscard(true)
	}

	const goBack = () => setStep(STEPS[stepIndex - 1])

	const handleCta = async () => {
		if (step !== 'name') {
			setStep(STEPS[stepIndex + 1])
			return
		}
		try {
			const trip = await createTrip.mutateAsync({ ...toPostTripPayload(draft), ...(source && { source }) })
			setCreatedTrip(trip)
		} catch (error) {
			toast.error(getErrorMessage(error, 'Failed to create trip. Please try again.'))
		}
	}

	if (createdTrip) {
		return (
			<PageScroll>
				<FlowContentArea>
					{/* The header's slot stays, empty, so the content keeps the vertical
					    start it had during the steps. */}
					<HeaderRow aria-hidden>
						<HeaderSlot />
					</HeaderRow>
					<ReadyStep
						trip={createdTrip}
						draft={draft}
						onGoToTrip={() => navigate(`/trips/${createdTrip.id}`)}
					/>
				</FlowContentArea>
			</PageScroll>
		)
	}

	return (
		<PageScroll>
			<FlowShell
				title="New Trip"
				showsHeaderBack={showsHeaderBack(step)}
				onBack={goBack}
				onClose={handleClose}
				steps={STEPS.map(candidate => ({
					key: candidate,
					label: stepLabel(candidate),
					state: stepIndicatorState(draft, candidate, step),
				}))}
				onSelectStep={setStep}
				bar={bottomBar(draft, step)}
				ctaPending={createTrip.isPending}
				ctaTestId={TestIds.tripCreate.stepCta(step)}
				onCta={handleCta}
			>
				{step === 'where' && <WhereStep draft={draft} onChange={setDraft} />}
				{step === 'when' && <WhenStep draft={draft} onChange={setDraft} />}
				{step === 'name' && <NameStep draft={draft} onChange={setDraft} />}
			</FlowShell>

			<ConfirmationModal
				isOpen={isConfirmingDiscard}
				onClose={() => setIsConfirmingDiscard(false)}
				onConfirm={leaveToTrips}
				title="Discard this trip?"
				message="Your places, dates and name won't be saved."
				confirmText="Discard"
			/>
		</PageScroll>
	)
}

export default CreateTripPage
